import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const POLL_MS = 60_000;

/**
 * Detects a new dash Worker deploy via CF version metadata (`GET /api/build`).
 * Boot id is captured from the first successful response (runtime, not build-time).
 * Disabled under Vite HMR so local reloads do not flash the banner.
 */
export function useAppUpdate(): { updateAvailable: boolean; reload: () => void } {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const bootIdRef = useRef<string | null>(null);
  const checkingRef = useRef(false);

  const check = useCallback(async () => {
    if (import.meta.env.DEV) return;
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const info = await api.build();
      if (!info.id) return;
      if (bootIdRef.current == null) {
        bootIdRef.current = info.id;
        return;
      }
      if (info.id !== bootIdRef.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network blips should not surface as update banners.
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    void check();
    const timer = window.setInterval(() => {
      void check();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
